import { CHRepository } from './user.model';

export interface LicenseDto {
  type: string;
  active: boolean;
  edition?: string;
  monthlyUploadLimit?: number;
  chRepositories?: CHRepository[];
  tenantAwareUrl?: string;
  url?: string;
  experienceTenantAwareUrl?: string;
  experienceUrl?: string;
  librariesTenantAwareUrl?: string;
  contentTemplatesFeature?: boolean;
  librariesUrl?: string;
  wrTenantAwareUrl?: string;
  wrUrl?: string;
  creationLimit?: number;
  storageLimit?: number;
  monthlyRequestLimit?: number;
  customSpecificationAvailable?: boolean;
  monthlyFormatConversions?: number;
  requestRateLimit?: number; // per minute
  identitiesLimit?: number;
}

export enum LicenseType {
  CONTENT_HUB = 'ContentHubLicense',
  WORK = 'WorkroomsLicense',
  DRIVE = 'DriveLicense',
  EXPERIENCE = 'ExperienceLicense',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  LIBRARIES = 'ExperienceLicense',
  X3D = 'X3DLicense',
  XSIM_SEARCH = 'XSimSearchLicense',
  BRANDED_LOGIN = 'BrandedLoginLicense',
  CONVERSION = 'ConversionLicense',
  AI_IMAGE_ANALYSIS = 'AIImageAnalysisLicense',
  API = 'APILicense',
  HOME = 'HomeLicense',
  NO_LICENSE = 'NoLicense'
}

export type BaseLicense = {
  type: LicenseType;
  active: boolean;
};

export type X3DLicense = BaseLicense & {
  monthlyUploadLimit: number;
};

export type ContentHubLicense = BaseLicense & {
  chRepositories: CHRepository[];
  // Deprecated: moved to own conversion license
  customSpecificationAvailable: boolean;
  // Deprecated: moved to own conversion license
  monthlyFormatConversions: number;
};

export type WorkroomsLicense = BaseLicense & {
  creationLimit: number;
  // Deprecated: moved to account.storageLimits.size
  storageLimit: number;
  wrUrl: string;
  wrTenantAwareUrl: string;
};

export type XSimSearchLicense = BaseLicense & {
  monthlyRequestLimit: number;
};

export type DriveLicense = BaseLicense & {
  edition: string;
};

export type ExperienceLicense = BaseLicense & {
  experienceUrl: string;
  experienceTenantAwareUrl: string;
  librariesUrl: string;
  librariesTenantAwareUrl: string;
  contentTemplatesFeature: boolean;
};

export type BrandedLoginLicense = BaseLicense & {
  libraryId: string;
  libraryAccessToken: string;
};

export type ConversionLicense = BaseLicense & {
  customSpecificationAvailable: boolean;
  monthlyFormatConversions: number;
};

export type AIImageAnalysisLicense = BaseLicense & {
  monthlyTokensLimit: number;
  analyticsFeature: 'BASIC' | 'EXTENDED';
};

export type APILicense = BaseLicense & {
  requestRateLimit: number; // per minute
  identitiesLimit: number;
};

export type HomeLicense = BaseLicense;

export type License =
  | ContentHubLicense
  | WorkroomsLicense
  | DriveLicense
  | ExperienceLicense
  | X3DLicense
  | XSimSearchLicense
  | BrandedLoginLicense
  | ConversionLicense
  | AIImageAnalysisLicense
  | APILicense
  | HomeLicense
  | BaseLicense;

export const isContentHubLicense = (license: License): license is ContentHubLicense => license.type === LicenseType.CONTENT_HUB;
export const isWorkroomsLicense = (license: License): license is WorkroomsLicense => license.type === LicenseType.WORK;
export const isDriveLicense = (license: License): license is DriveLicense => license.type === LicenseType.DRIVE;
export const isExperienceLicense = (license: License): license is ExperienceLicense => license.type === LicenseType.EXPERIENCE;
export const isX3DLicense = (license: License): license is X3DLicense => license.type === LicenseType.X3D;
export const isXSimSearchLicense = (license: License): license is XSimSearchLicense => license.type === LicenseType.XSIM_SEARCH;
export const isBrandedLoginLicense = (license: License): license is BrandedLoginLicense => license.type === LicenseType.BRANDED_LOGIN;
export const isAIImageAnalysisLicense = (license: License): license is AIImageAnalysisLicense => license.type === LicenseType.AI_IMAGE_ANALYSIS;

export const isConversionLicense = (license: License): license is ConversionLicense => license.type === LicenseType.CONVERSION;

export const isAPILicense = (license: License): license is APILicense => license.type === LicenseType.API;

export const isHomeLicense = (license: License): license is HomeLicense => license.type === LicenseType.HOME;

export const isDomainLicense = (license: License) => isContentHubLicense(license) || isExperienceLicense(license) || isWorkroomsLicense(license);

export const isFeatureLicense = (license: License) => !isDomainLicense(license);
